import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import dayjs from 'dayjs';
import { Button, Space, message, Tag, Tooltip, Popconfirm, Modal, Input, Checkbox as AntCheckbox } from 'antd';
import { PlusOutlined, SearchOutlined, ColumnHeightOutlined, UploadOutlined, DownloadOutlined } from '@ant-design/icons';
import { useNavigate, useLocation, useOutletContext } from 'react-router-dom';
import { taskService, Task } from '../services/tasks';
import { useTableColumnSearch } from '../hooks/useTableColumnSearch';
import { useColumnVisibility } from '../hooks/useColumnVisibility';
import { useListingTableFocus } from '../hooks/useListingTableFocus';
import { useListingColumnsPersistence } from '../hooks/useListingColumnsPersistence';
import { useListingColumnWidthsPersistence } from '../hooks/useListingColumnWidthsPersistence';
import { useListingSortPersistence } from '../hooks/useListingSortPersistence';
import { useAuth } from '../contexts/AuthContext';
import { hasPermission } from '../utils/permissions';
import '../styles/resizable-table.css';
import {
  Table as FlowbiteTable,
  TableHead,
  TableHeadCell,
  TableBody,
  TableRow,
  TableCell,
  Checkbox,
  Alert,
} from 'flowbite-react';
import { Resizable } from 'react-resizable';

type LayoutHeaderContext = {
  setHeaderContent: (content: React.ReactNode) => void;
};
type FilterType = 'all' | 'ongoing' | 'complete';
type SortOrder = 'ascend' | 'descend' | null;

interface FlowbiteResizableHeaderProps {
  width?: number | string;
  maxWidth?: number;
  onResize?: (e: any, data: { size: { width: number } }) => void;
  onResizeStop?: (e: any, data: { size: { width: number } }) => void;
  onClick?: () => void;
  className?: string;
  children: React.ReactNode;
}

const FlowbiteResizableHeader: React.FC<FlowbiteResizableHeaderProps> = ({
  width,
  maxWidth,
  onResize,
  onResizeStop,
  onClick,
  className,
  children,
}) => {
  if (typeof width !== 'number' || !onResize) {
    return (
      <TableHeadCell
        className={className}
        style={width ? { width } : undefined}
        onClick={onClick}
      >
        {children}
      </TableHeadCell>
    );
  }

  return (
    <Resizable
      width={width}
      height={0}
      onResize={(e, data) => {
        const newWidth = Math.max(data.size.width, 50);
        onResize(e, { ...data, size: { ...data.size, width: newWidth } });
      }}
      onResizeStop={(e, data) => {
        if (!onResizeStop) {
          return;
        }
        const newWidth = Math.max(data.size.width, 50);
        onResizeStop(e, { ...data, size: { ...data.size, width: newWidth } });
      }}
      resizeHandles={['e']}
      handle={
        <span
          className="resizable-table-handle"
          style={{ height: '100%' }}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        />
      }
      draggableOpts={{ enableUserSelectHack: false }}
      maxConstraints={typeof maxWidth === 'number' ? [maxWidth, 0] : undefined}
      minConstraints={[50, 0]}
    >
      <TableHeadCell
        className={className ? `relative ${className}` : 'relative'}
        style={{ width }}
        onClick={onClick}
      >
        {children}
      </TableHeadCell>
    </Resizable>
  );
};

export const Tasks: React.FC = () => {
  const listingKey = 'tasks';
  const [tasks, setTasks] = useState<Task[]>([]);
  const [submitNotice, setSubmitNotice] = useState<{ type: 'success' | 'failure'; message: string } | null>(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState<FilterType>('all');
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const [excludedRowKeys, setExcludedRowKeys] = useState<React.Key[]>([]);
  const [deleting, setDeleting] = useState(false);
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<SortOrder>(null);
  const [columnWidthByKey, setColumnWidthByKey] = useState<Record<string, number>>({});
  const [columnsModalOpen, setColumnsModalOpen] = useState(false);
  const [columnsSearch, setColumnsSearch] = useState('');
  const [pendingVisibleKeys, setPendingVisibleKeys] = useState<React.Key[]>([]);
  const tableScrollRef = useRef<HTMLDivElement | null>(null);
  const [availableWidth, setAvailableWidth] = useState<number | null>(null);
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [statusCountsFromApi, setStatusCountsFromApi] = useState<{ all: number; ongoing: number; complete: number } | null>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewportHeight, setViewportHeight] = useState<number | null>(null);
  const [scrollbarWidth, setScrollbarWidth] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const { setHeaderContent } = useOutletContext<LayoutHeaderContext>();

  useEffect(() => {
    const stateNotice = (location.state as { notice?: { type: 'success' | 'failure'; message: string } } | null)?.notice;
    if (stateNotice) {
      setSubmitNotice(stateNotice);
      navigate(location.pathname + location.search, { replace: true });
    }
  }, [location, navigate]);
  const { user } = useAuth();
  const canView = hasPermission(user, 'projects.tasks.view');
  const canCreate = hasPermission(user, 'projects.tasks.create');
  const canDelete = hasPermission(user, 'projects.tasks.delete');
  const statusCounts = useMemo(() => {
    if (statusCountsFromApi) {
      return statusCountsFromApi;
    }
    const counts = { all: tasks.length, ongoing: 0, complete: 0 };
    tasks.forEach((task) => {
      if (task.status && ['Plan', 'Progress', 'Review', 'Approve'].includes(task.status)) {
        counts.ongoing += 1;
        return;
      }
      if (task.status === 'Completed') {
        counts.complete += 1;
      }
    });
    return counts;
  }, [tasks, statusCountsFromApi]);


  useEffect(() => {
    if (!canView) {
      setTasks([]);
      return;
    }
    fetchData();
  }, [canView]);

  useEffect(() => {
    setHeaderContent(
      <span style={{ fontSize: 16, fontWeight: 600, color: '#111827' }}>
        Tasks
      </span>
    );
    return () => setHeaderContent(null);
  }, [setHeaderContent]);

  const fetchData = async () => {
    if (!canView) {
      return;
    }
    setLoading(true);
    try {
      const response = await taskService.getAll();
      const { items, total, statusCounts: apiStatusCounts } = normalizeTasksResponse(response);
      setTasks(items);
      const resolvedTotal = typeof total === 'number' && total > 0 ? total : items.length;
      setTotalCount(resolvedTotal);
      setStatusCountsFromApi(apiStatusCounts);
    } catch {
      message.error('Failed to load tasks data');
    } finally {
      setLoading(false);
    }
  };

  const normalizeTasksResponse = (response: any) => {
    const raw = response?.data ?? response;
    const meta = raw?.meta;
    const payload = raw?.data && !Array.isArray(raw?.data) ? raw.data : raw;
    const items = Array.isArray(payload)
      ? payload
      : Array.isArray(payload?.data)
        ? payload.data
        : [];
    const total = Number(payload?.total ?? meta?.total ?? 0);
    const nextPageExists = false;
    const statusCountsMeta = meta?.status_counts;
    const statusCounts = statusCountsMeta
      ? {
          all: total || 0,
          ongoing: Number(statusCountsMeta.ongoing ?? 0),
          complete: Number(statusCountsMeta.complete ?? 0),
        }
      : null;
    return { items, nextPageExists, total, statusCounts };
  };

  const handleImportNavigate = () => {
    navigate('/tasks/import');
  };

  const handleBatchExport = async () => {
    if (!selectAll && selectedRowKeys.length === 0) {
      return;
    }
    try {
      const filters = filter === 'all' ? {} : { status: filter };
      const payload = selectAll
        ? { select_all: true, excluded_ids: excludedRowKeys.map((key) => Number(key)), filters }
        : { ids: selectedRowKeys.map((key) => Number(key)), filters };
      const blob = await taskService.export(payload);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `task-export-${dayjs().format('YYYYMMDD-HHmmss')}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      message.error('Failed to export tasks');
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await taskService.delete(id);
      message.success('Task deleted');
      fetchData();
    } catch {
      message.error('Failed to delete task');
    }
  };

  const handleBatchDelete = async () => {
    if (!selectAll && selectedRowKeys.length === 0) {
      return;
    }
    setDeleting(true);
    try {
      const filters = filter === 'all' ? {} : { status: filter };
      const payload = selectAll
        ? { select_all: true, excluded_ids: excludedRowKeys.map((key) => Number(key)), filters }
        : { ids: selectedRowKeys.map((key) => Number(key)), filters };
      await taskService.batchDelete(payload);
      message.success('Tasks deleted');
      setSelectedRowKeys([]);
      setSelectAll(false);
      setExcludedRowKeys([]);
      fetchData();
    } catch {
      message.error('Failed to delete tasks');
    } finally {
      setDeleting(false);
    }
  };

  const formatTaskNumber = (id: number, referenceNumber?: string) => {
    return referenceNumber || `#${id.toString().padStart(7, '0')}`;
  };

  const formatDateValue = (date?: string) => (date ? dayjs(date).format('DD/MM/YYYY') : '-');
  const formatYearMonthValue = (value?: string | number | null) => {
    if (value == null) return '';
    const raw = String(value).trim();
    if (!raw) return '';
    if (/^(0[1-9]|1[0-2])\/\d{4}$/.test(raw)) return raw;
    const ymd = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (ymd) return `${ymd[2]}/${ymd[1]}`;
    if (/^\d{4}$/.test(raw)) return `01/${raw}`;
    return raw;
  };
  const yearMonthSortKey = (value?: string | number | null) => {
    const formatted = formatYearMonthValue(value);
    const match = formatted.match(/^(0[1-9]|1[0-2])\/(\d{4})$/);
    if (!match) return 0;
    return Number(match[2]) * 100 + Number(match[1]);
  };
  const compareYearMonth = (a?: string | number | null, b?: string | number | null) =>
    yearMonthSortKey(a) - yearMonthSortKey(b);

  const getTaskTypeName = (record: Task) => {
    const type = record.taskType || (record as any).task_type;
    return type?.name || '-';
  };

  const getTaskTemplateName = (record: Task) => {
    const template = record.taskTemplate || (record as any).task_template;
    return template?.name || '-';
  };

  const getQuotationLabel = (record: Task) => {
    const quotation = record.quotation;
    if (quotation?.quotation_number) {
      return quotation.quotation_number;
    }
    if (quotation?.id) {
      return `#${quotation.id}`;
    }
    if (record.quotation_id) {
      return `#${record.quotation_id}`;
    }
    return '-';
  };

  const getInvoiceLabel = (record: Task) => {
    const quotation = record.quotation as any;
    const invoice = quotation?.invoice;
    if (invoice?.invoice_number) {
      return invoice.invoice_number;
    }
    if (invoice?.id) {
      return `#${invoice.id}`;
    }
    if (quotation?.invoice_id) {
      return `#${quotation.invoice_id}`;
    }
    return '-';
  };

  const getChecklistLabel = (record: Task) => {
    const subTasks = (record as any).subTasks || (record as any).sub_tasks || [];
    const total = subTasks.length;
    if (!total) {
      return '-';
    }
    const completed = subTasks.filter((task: any) => task.completed).length;
    return `${completed}/${total}`;
  };

  const getDraftLabel = (record: Task) => {
    const draftReports = (record as any).draftReports || (record as any).draft_reports || [];
    return String(draftReports.length);
  };

  const getCommentLabel = (record: Task) => {
    const comments = (record as any).comments || [];
    return comments.length > 0 ? 'Yes' : 'No';
  };

  const getReviewValue = (reviews: any[]) => {
    if (!reviews.length) {
      return '-';
    }
    const review = reviews[0];
    if (typeof review?.rating === 'number' && Number.isFinite(review.rating)) {
      const rating = Math.round(review.rating);
      return `${rating} Star`;
    }
    if (review?.review) {
      return review.review;
    }
    return '-';
  };

  const getCustomerReviewLabel = (record: Task) => {
    const reviews = (record as any).reviews || [];
    const customerReviews = reviews.filter((review: any) => review.customer_id);
    return getReviewValue(customerReviews);
  };

  const getManagerReviewLabel = (record: Task) => {
    const reviews = (record as any).reviews || [];
    const staffReviews = reviews.filter((review: any) => !review.customer_id);
    return getReviewValue(staffReviews);
  };

  const getStaffFeedbackLabel = (record: Task) => {
    const reviews = (record as any).reviews || [];
    const staffReviews = reviews.filter((review: any) => !review.customer_id);
    return staffReviews.length > 0 ? 'Yes' : 'No';
  };

  const getReferenceNumbersLabel = (items: any) => {
    if (!items || (Array.isArray(items) && items.length === 0)) {
      return '-';
    }
    const list = Array.isArray(items) ? items : [items];
    const labels = list
      .map((item) => item?.reference_number || item?.referenceNumber || (item?.id ? `#${item.id}` : null))
      .filter(Boolean);
    return labels.length ? labels.join(', ') : '-';
  };

  const getLetterLabel = (record: Task) => {
    const letters = (record as any).letters || (record as any).letter;
    return getReferenceNumbersLabel(letters);
  };

  const getDispatchLabel = (record: Task) => {
    const dispatches = (record as any).dispatches || (record as any).dispatch;
    return getReferenceNumbersLabel(dispatches);
  };

  const getActualHours = (record: Task) => {
    if (record.actual_hour != null) {
      return record.actual_hour;
    }
    const timelogs = (record as any).timelogs || [];
    if (!timelogs.length) {
      return null;
    }
    const total = timelogs.reduce((sum: number, log: any) => sum + Number(log.hour || 0), 0);
    return total || null;
  };

  const getReferenceLabel = (record: Task) => {
    if (record.type === 'business') {
      if (record.business?.name) return record.business.name;
      if (record.reference_id) return `Business #${record.reference_id}`;
      return '-';
    }
    if (record.type === 'personal') {
      if (record.customer?.name) return record.customer.name;
      if (record.reference_id) return `Customer #${record.reference_id}`;
      return '-';
    }
    return record.customer?.name || record.business?.name || '-';
  };

  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'Plan':
        return 'blue';
      case 'Progress':
        return 'orange';
      case 'Review':
        return 'purple';
      case 'Approve':
        return 'cyan';
      case 'Completed':
        return 'green';
      default:
        return 'default';
    }
  };

  const getCellValue = (record: Task, col: any) => {
    if (!col.dataIndex) {
      return undefined;
    }
    if (Array.isArray(col.dataIndex)) {
      return col.dataIndex.reduce((acc: any, key: string) => (acc == null ? undefined : acc[key]), record);
    }
    return (record as any)[col.dataIndex];
  };

  const compareReferenceNumbers = (a?: string, b?: string) => {
    if (!a && !b) return 0;
    if (!a) return 1;
    if (!b) return -1;
    return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
  };

  const filteredTasks = useMemo(() => {
    if (filter === 'all') {
      return tasks;
    } else if (filter === 'ongoing') {
      return tasks.filter(task => task.status && ['Plan', 'Progress', 'Review', 'Approve'].includes(task.status));
    } else if (filter === 'complete') {
      return tasks.filter(task => task.status === 'Completed');
    }
    return tasks;
  }, [tasks, filter]);

  const baseColumns = useMemo(
    () => [
    {
      title: 'Task No.',
      key: 'task_number',
      width: 120,
      sorter: (a: Task, b: Task) =>
        compareReferenceNumbers(
          formatTaskNumber(a.id, a.reference_number),
          formatTaskNumber(b.id, b.reference_number)
        ),
      render: (_: unknown, record: Task) => {
        const taskNumber = formatTaskNumber(record.id, record.reference_number);
        return (
          <Tooltip title={taskNumber} placement="topLeft">
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {taskNumber}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Customer',
      dataIndex: 'customer',
      key: 'customer',
      width: 180,
      sorter: (a: Task, b: Task) => getReferenceLabel(a).localeCompare(getReferenceLabel(b)),
      render: (_: any, record: Task) => (
        <Tooltip title={getReferenceLabel(record)} placement="topLeft">
          <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {getReferenceLabel(record)}
          </div>
        </Tooltip>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'task_type',
      key: 'task_type',
      width: 150,
      visible: false,
      sorter: (a: Task, b: Task) => {
        return getTaskTypeName(a).localeCompare(getTaskTypeName(b));
      },
      render: (_: any, record: Task) => {
        const typeName = getTaskTypeName(record);
        return (
          <Tooltip title={typeName} placement="topLeft">
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {typeName}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Template',
      dataIndex: 'task_template',
      key: 'task_template',
      width: 180,
      visible: false,
      sorter: (a: Task, b: Task) => {
        return getTaskTemplateName(a).localeCompare(getTaskTemplateName(b));
      },
      render: (_: any, record: Task) => {
        const templateName = getTaskTemplateName(record);
        return (
          <Tooltip title={templateName} placement="topLeft">
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {templateName}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Task',
      dataIndex: 'task_template',
      key: 'task',
      width: 180,
      visible: false,
      sorter: (a: Task, b: Task) => getTaskTemplateName(a).localeCompare(getTaskTemplateName(b)),
      render: (_: any, record: Task) => {
        const templateName = getTaskTemplateName(record);
        return (
          <Tooltip title={templateName} placement="topLeft">
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {templateName}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Year',
      dataIndex: 'year',
      key: 'year',
      width: 100,
      visible: false,
      sorter: (a: Task, b: Task) => compareYearMonth(a.year, b.year),
      render: (year: string | number | undefined) => {
        const yearStr = formatYearMonthValue(year) || '-';
        return (
          <Tooltip title={yearStr} placement="topLeft">
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {yearStr}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Period',
      dataIndex: 'year_end',
      key: 'year_end',
      width: 120,
      visible: false,
      sorter: (a: Task, b: Task) => compareYearMonth(a.year, b.year),
      render: (_: any, record: Task) => {
        const value = formatYearMonthValue(record.year) || '-';
        return (
          <Tooltip title={value} placement="topLeft">
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {value}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Create',
      dataIndex: 'created_date',
      key: 'created_date',
      width: 120,
      visible: false,
      sorter: (a: Task, b: Task) => {
        if (!a.created_date && !b.created_date) return 0;
        if (!a.created_date) return 1;
        if (!b.created_date) return -1;
        return new Date(a.created_date).getTime() - new Date(b.created_date).getTime();
      },
      render: (date: string) => {
        const dateStr = formatDateValue(date);
        return (
          <Tooltip title={dateStr} placement="topLeft">
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {dateStr}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Due',
      dataIndex: 'due_date',
      key: 'due_date',
      width: 120,
      sorter: (a: Task, b: Task) => {
        if (!a.due_date && !b.due_date) return 0;
        if (!a.due_date) return 1;
        if (!b.due_date) return -1;
        return new Date(a.due_date).getTime() - new Date(b.due_date).getTime();
      },
      render: (date: string) => {
        const dateStr = formatDateValue(date);
        return (
          <Tooltip title={dateStr} placement="topLeft">
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {dateStr}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Plan',
      dataIndex: 'plan_date',
      key: 'plan_date',
      width: 120,
      visible: false,
      sorter: (a: Task, b: Task) => {
        if (!a.plan_date && !b.plan_date) return 0;
        if (!a.plan_date) return 1;
        if (!b.plan_date) return -1;
        return new Date(a.plan_date).getTime() - new Date(b.plan_date).getTime();
      },
      render: (date: string) => {
        const dateStr = formatDateValue(date);
        return (
          <Tooltip title={dateStr} placement="topLeft">
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {dateStr}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Deadline',
      dataIndex: 'deadline',
      key: 'deadline',
      width: 120,
      visible: false,
      sorter: (a: Task, b: Task) => {
        if (!a.deadline && !b.deadline) return 0;
        if (!a.deadline) return 1;
        if (!b.deadline) return -1;
        return new Date(a.deadline).getTime() - new Date(b.deadline).getTime();
      },
      render: (date: string) => {
        const dateStr = formatDateValue(date);
        return (
          <Tooltip title={dateStr} placement="topLeft">
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {dateStr}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Estimate',
      dataIndex: 'plan_hour',
      key: 'plan_hour',
      width: 110,
      visible: false,
      sorter: (a: Task, b: Task) => (a.plan_hour || 0) - (b.plan_hour || 0),
      render: (hours: number) => {
        const value = hours != null ? hours : '-';
        return (
          <Tooltip title={String(value)} placement="topLeft">
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {value}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Actual',
      dataIndex: 'actual_hour',
      key: 'actual_hour',
      width: 110,
      visible: false,
      sorter: (a: Task, b: Task) => {
        const aValue = getActualHours(a) || 0;
        const bValue = getActualHours(b) || 0;
        return aValue - bValue;
      },
      render: (_: number, record: Task) => {
        const value = getActualHours(record);
        const label = value != null ? value : '-';
        return (
          <Tooltip title={String(label)} placement="topLeft">
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {label}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Letter No.',
      key: 'letter_number',
      width: 140,
      visible: false,
      render: (_: unknown, record: Task) => {
        const label = getLetterLabel(record);
        return (
          <Tooltip title={label} placement="topLeft">
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {label}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Dispatch No.',
      key: 'dispatch_number',
      width: 140,
      visible: false,
      render: (_: unknown, record: Task) => {
        const label = getDispatchLabel(record);
        return (
          <Tooltip title={label} placement="topLeft">
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {label}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Quotation No.',
      key: 'quotation_number',
      width: 150,
      visible: false,
      sorter: (a: Task, b: Task) => compareReferenceNumbers(getQuotationLabel(a), getQuotationLabel(b)),
      render: (_: unknown, record: Task) => {
        const label = getQuotationLabel(record);
        return (
          <Tooltip title={label} placement="topLeft">
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {label}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Invoice No.',
      key: 'invoice_number',
      width: 150,
      visible: false,
      sorter: (a: Task, b: Task) => compareReferenceNumbers(getInvoiceLabel(a), getInvoiceLabel(b)),
      render: (_: unknown, record: Task) => {
        const label = getInvoiceLabel(record);
        return (
          <Tooltip title={label} placement="topLeft">
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {label}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Team',
      key: 'team',
      width: 140,
      visible: false,
      sorter: (a: Task, b: Task) => (a.team?.name || '').localeCompare(b.team?.name || ''),
      render: (_: unknown, record: Task) => {
        const label = record.team?.name || '-';
        return (
          <Tooltip title={label} placement="topLeft">
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {label}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Staff',
      key: 'staff',
      width: 140,
      visible: false,
      sorter: (a: Task, b: Task) => (a.staff?.name || '').localeCompare(b.staff?.name || ''),
      render: (_: unknown, record: Task) => {
        const label = record.staff?.name || '-';
        return (
          <Tooltip title={label} placement="topLeft">
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {label}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Checklist',
      key: 'checklist',
      width: 120,
      visible: false,
      sorter: (a: Task, b: Task) => {
        const aLabel = getChecklistLabel(a);
        const bLabel = getChecklistLabel(b);
        return aLabel.localeCompare(bLabel);
      },
      render: (_: unknown, record: Task) => {
        const label = getChecklistLabel(record);
        return (
          <Tooltip title={label} placement="topLeft">
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {label}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Draft',
      key: 'draft',
      width: 100,
      visible: false,
      sorter: (a: Task, b: Task) => Number(getDraftLabel(a)) - Number(getDraftLabel(b)),
      render: (_: unknown, record: Task) => {
        const label = getDraftLabel(record);
        return (
          <Tooltip title={label} placement="topLeft">
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {label}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Comment',
      key: 'comment',
      width: 110,
      visible: false,
      sorter: (a: Task, b: Task) => getCommentLabel(a).localeCompare(getCommentLabel(b)),
      render: (_: unknown, record: Task) => {
        const label = getCommentLabel(record);
        return (
          <Tooltip title={label} placement="topLeft">
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {label}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Customer Review',
      key: 'customer_review',
      width: 160,
      visible: false,
      sorter: (a: Task, b: Task) => getCustomerReviewLabel(a).localeCompare(getCustomerReviewLabel(b)),
      render: (_: unknown, record: Task) => {
        const label = getCustomerReviewLabel(record);
        return (
          <Tooltip title={label} placement="topLeft">
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {label}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Manager Review',
      key: 'manager_review',
      width: 160,
      visible: false,
      sorter: (a: Task, b: Task) => getManagerReviewLabel(a).localeCompare(getManagerReviewLabel(b)),
      render: (_: unknown, record: Task) => {
        const label = getManagerReviewLabel(record);
        return (
          <Tooltip title={label} placement="topLeft">
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {label}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Staff Feedback',
      key: 'staff_feedback',
      width: 140,
      visible: false,
      sorter: (a: Task, b: Task) => getStaffFeedbackLabel(a).localeCompare(getStaffFeedbackLabel(b)),
      render: (_: unknown, record: Task) => {
        const label = getStaffFeedbackLabel(record);
        return (
          <Tooltip title={label} placement="topLeft">
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {label}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Status',
      key: 'status',
      width: 120,
      visible: false,
      sorter: (a: Task, b: Task) => (a.status || 'Plan').localeCompare(b.status || 'Plan'),
      render: (_: unknown, record: Task) => (
        <Tag color={getStatusColor(record.status)}>
          {record.status || 'Plan'}
        </Tag>
      ),
    },
    ],
    []
  );

  const {
    columnConfig,
    visibleColumns,
    visibleKeys,
    handleColumnVisibilityChange,
    setColumnConfig,
  } = useColumnVisibility(baseColumns);
  const maxVisibleColumns = 10;
  const { persistVisibleKeys } = useListingColumnsPersistence(
    listingKey,
    baseColumns,
    maxVisibleColumns,
    setColumnConfig
  );
  const { persistColumnWidths } = useListingColumnWidthsPersistence(
    listingKey,
    baseColumns,
    setColumnWidthByKey
  );
  const { persistSort } = useListingSortPersistence(
    listingKey,
    baseColumns,
    setSortKey,
    setSortOrder
  );
  const columnGroups = useMemo(
    () => [
      {
        title: 'Details',
        keys: [
          'task_number',
          'customer',
          'task',
          'year_end',
          'created_date',
          'due_date',
          'plan_date',
          'deadline',
          'plan_hour',
          'actual_hour',
          'letter_number',
          'dispatch_number',
          'quotation_number',
          'invoice_number',
          'team',
          'staff',
        ],
      },
      {
        title: 'Checklist',
        keys: ['checklist'],
      },
      {
        title: 'Draft',
        keys: ['draft'],
      },
      {
        title: 'Review',
        keys: ['customer_review', 'manager_review'],
      },
    ],
    []
  );
  const defaultVisibleKeys = useMemo(
    () => baseColumns.filter((col) => !('visible' in col) || (col as { visible?: boolean }).visible !== false).map((col) => col.key),
    [baseColumns]
  );
  const handleColumnVisibilityChangeLimited = useCallback(
    (keys: React.Key[]) => {
      if (keys.length > maxVisibleColumns) {
        message.warning(`You can select up to ${maxVisibleColumns} columns.`);
        return;
      }
      handleColumnVisibilityChange(keys);
      persistVisibleKeys(keys);
    },
    [handleColumnVisibilityChange, maxVisibleColumns, persistVisibleKeys]
  );
  const columnSelectionLocked = pendingVisibleKeys.length >= maxVisibleColumns;
  const filteredColumnConfig = useMemo(() => {
    if (!columnsSearch.trim()) {
      return columnConfig;
    }
    const query = columnsSearch.toLowerCase();
    return columnConfig.filter((col) => col.title.toLowerCase().includes(query));
  }, [columnConfig, columnsSearch]);
  const columnConfigByKey = useMemo(
    () => new Map(columnConfig.map((col) => [col.key, col])),
    [columnConfig]
  );

  const getSearchValueForColumn = useCallback((col: any) => {
    if (col.key === 'task_number') return (record: Task) => formatTaskNumber(record.id, record.reference_number);
    if ('dataIndex' in col && col.dataIndex === 'customer') return (record: Task) => getReferenceLabel(record);
    if ('dataIndex' in col && col.dataIndex === 'task_type') {
      return (record: Task) => getTaskTypeName(record);
    }
    if ('dataIndex' in col && col.dataIndex === 'task_template') {
      return (record: Task) => getTaskTemplateName(record);
    }
    if (col.key === 'task') return (record: Task) => getTaskTemplateName(record);
    if ('dataIndex' in col && col.dataIndex === 'year') return (record: Task) => formatYearMonthValue(record.year);
    if (col.key === 'year_end') return (record: Task) => formatYearMonthValue(record.year);
    if (col.key === 'created_date') return (record: Task) => formatDateValue(record.created_date);
    if ('dataIndex' in col && col.dataIndex === 'due_date') return (record: Task) => formatDateValue(record.due_date);
    if (col.key === 'plan_date') return (record: Task) => formatDateValue(record.plan_date);
    if (col.key === 'deadline') return (record: Task) => formatDateValue(record.deadline);
    if (col.key === 'plan_hour') return (record: Task) => record.plan_hour;
    if (col.key === 'actual_hour') return (record: Task) => getActualHours(record);
    if (col.key === 'letter_number') return (record: Task) => getLetterLabel(record);
    if (col.key === 'dispatch_number') return (record: Task) => getDispatchLabel(record);
    if (col.key === 'quotation_number') return (record: Task) => getQuotationLabel(record);
    if (col.key === 'invoice_number') return (record: Task) => getInvoiceLabel(record);
    if (col.key === 'team') return (record: Task) => record.team?.name || '';
    if (col.key === 'staff') return (record: Task) => record.staff?.name || '';
    if (col.key === 'checklist') return (record: Task) => getChecklistLabel(record);
    if (col.key === 'draft') return (record: Task) => getDraftLabel(record);
    if (col.key === 'comment') return (record: Task) => getCommentLabel(record);
    if (col.key === 'customer_review') return (record: Task) => getCustomerReviewLabel(record);
    if (col.key === 'manager_review') return (record: Task) => getManagerReviewLabel(record);
    if (col.key === 'staff_feedback') return (record: Task) => getStaffFeedbackLabel(record);
    if (col.key === 'status') return (record: Task) => record.status || 'Plan';
    const key = String(col.key || '').toLowerCase();
    if (typeof col.dataIndex === 'string') {
      const dataIndex = col.dataIndex.toLowerCase();
      if (dataIndex.includes('date') || dataIndex.includes('due')) {
        return (record: Task) => formatDateValue((record as any)[col.dataIndex]);
      }
    }
    if (key.includes('date') || key.includes('due')) {
      return (record: Task) => formatDateValue((record as any)[col.key]);
    }
    return undefined as unknown as (record: Task) => string | number | null | undefined;
  }, []);

  const searchColumns = useMemo(
    () =>
      visibleColumns.map((col) => ({
        ...col,
        getSearchValue: getSearchValueForColumn(col),
      })),
    [visibleColumns, getSearchValueForColumn]
  );

  const memoizedColumns = useMemo(
    () =>
      visibleColumns.map((col) => ({
        ...col,
        width: col.width,
        getSearchValue: getSearchValueForColumn(col),
      })),
    [visibleColumns, getSearchValueForColumn]
  );

  const { columnsWithSearch, filteredData, searchRowCells } = useTableColumnSearch<Task>(
    filteredTasks,
    memoizedColumns as any,
    searchColumns as any,
    { splitHeader: true, listingKey }
  );

  const tableColumns = columnsWithSearch as any[];
  const totalValue = filteredData.length;
  const totalLabel = `Total: ${totalValue} ${totalValue === 1 ? 'task' : 'tasks'}`;
  const totalColumnCount = tableColumns.length + 1;
  const minColumnWidth = 50;
  const baseColumnWidths = useMemo(
    () =>
      tableColumns.map((col) => {
        const key = String(col.key);
        const cached = columnWidthByKey[key];
        if (typeof cached === 'number') {
          return cached;
        }
        if (typeof col.width === 'number') {
          return col.width;
        }
        if (typeof col.width === 'string') {
          const parsed = Number.parseFloat(col.width);
          if (Number.isFinite(parsed)) {
            return parsed;
          }
        }
        return 150;
      }),
    [tableColumns, columnWidthByKey]
  );
  const columnWidths = useMemo(() => {
    if (availableWidth == null) {
      return baseColumnWidths;
    }
    const total = baseColumnWidths.reduce((sum, width) => sum + width, 0) || 1;
    if (total <= availableWidth) {
      return baseColumnWidths;
    }
    const scale = availableWidth / total;
    return baseColumnWidths.map((width) => Math.max(minColumnWidth, Math.floor(width * scale)));
  }, [baseColumnWidths, availableWidth]);
  const handleResize = useCallback(
    (targetIndex: number) => (_e: unknown, data: { size: { width: number } }) => {
      const keys = tableColumns.map((col) => String(col.key));
      const current = columnWidths.map((width) => Math.max(minColumnWidth, Math.round(width)));
      const totalAvailable = availableWidth ?? current.reduce((sum, width) => sum + width, 0);
      const rightIndexes = current.map((_width, index) => index).filter((index) => index > targetIndex);
      if (rightIndexes.length === 0) {
        return;
      }
      const rightReducible = rightIndexes.reduce(
        (sum, index) => sum + Math.max(0, current[index] - minColumnWidth),
        0
      );
      const maxTarget = Math.max(minColumnWidth, current[targetIndex] + rightReducible);
      const nextWidth = Math.min(Math.max(Math.round(data.size.width), minColumnWidth), maxTarget);
      const delta = nextWidth - current[targetIndex];
      if (delta === 0) {
        return;
      }

      const updated = [...current];
      updated[targetIndex] = nextWidth;

      if (delta > 0) {
        const reducible = rightIndexes.map((index) => Math.max(0, current[index] - minColumnWidth));
        let remaining = delta;
        const totalReducible = reducible.reduce((sum, value) => sum + value, 0);
        if (totalReducible === 0) {
          return;
        }
        rightIndexes.forEach((index, position) => {
          const room = reducible[position];
          if (room === 0) return;
          const take = Math.min(room, Math.round((delta * room) / totalReducible));
          updated[index] = current[index] - take;
          remaining -= take;
        });
        if (remaining > 0) {
          for (let idx = 0; idx < rightIndexes.length && remaining > 0; idx += 1) {
            const index = rightIndexes[idx];
            if (updated[index] > minColumnWidth) {
              updated[index] -= 1;
              remaining -= 1;
            }
          }
        }
      } else {
        const add = -delta;
        const totalRight = rightIndexes.reduce((sum, index) => sum + current[index], 0);
        let remaining = add;
        rightIndexes.forEach((index) => {
          const give = Math.round((add * current[index]) / (totalRight || 1));
          updated[index] = current[index] + give;
          remaining -= give;
        });
        if (remaining !== 0) {
          for (let idx = 0; idx < rightIndexes.length && remaining !== 0; idx += 1) {
            const index = rightIndexes[idx];
            updated[index] += remaining > 0 ? 1 : -1;
            remaining += remaining > 0 ? -1 : 1;
          }
        }
      }

      const nextWidths: Record<string, number> = {};
      keys.forEach((key, index) => {
        nextWidths[key] = updated[index];
      });
      setColumnWidthByKey((prev) => {
        const merged = { ...prev, ...nextWidths };
        persistColumnWidths(merged);
        return merged;
      });
    },
    [availableWidth, columnWidths, minColumnWidth, persistColumnWidths, tableColumns]
  );

  useEffect(() => {
    const updateAvailableWidth = () => {
      const containerWidth = tableScrollRef.current?.clientWidth ?? 0;
      const borderAdjust = Math.max(totalColumnCount - 1, 0);
      const nextAvailable = Math.max(containerWidth - 48 - borderAdjust, 0);
      setAvailableWidth(nextAvailable || null);
    };

    updateAvailableWidth();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateAvailableWidth);
      return () => window.removeEventListener('resize', updateAvailableWidth);
    }
    const observer = new ResizeObserver(updateAvailableWidth);
    if (tableScrollRef.current) {
      observer.observe(tableScrollRef.current);
    }
    return () => observer.disconnect();
  }, [totalColumnCount]);

  useEffect(() => {
    const container = tableScrollRef.current;
    if (!container) {
      return;
    }
    const updateHeight = () => {
      const rect = container.getBoundingClientRect();
      const nextHeight = Math.max(window.innerHeight - rect.top - 24, 200);
      setViewportHeight(nextHeight);
      const nextScrollbarWidth = Math.max(container.offsetWidth - container.clientWidth, 0);
      setScrollbarWidth(nextScrollbarWidth);
    };
    updateHeight();
    const mutationObserver = new MutationObserver(() => {
      updateHeight();
    });
    mutationObserver.observe(container, { childList: true, subtree: true, attributes: true });
    const handleScroll = () => {
      setScrollTop(container.scrollTop);
    };
    container.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', updateHeight);
    return () => {
      mutationObserver.disconnect();
      container.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', updateHeight);
    };
  }, [filteredData.length]);


  const sortedData = useMemo(() => {
    if (!sortKey || !sortOrder) {
      return filteredData;
    }
    const column = tableColumns.find((col: any) => String(col.key) === sortKey);
    const sorter = column?.sorter;
    if (typeof sorter !== 'function') {
      return filteredData;
    }
    const sorted = [...filteredData].sort(sorter);
    return sortOrder === 'descend' ? sorted.reverse() : sorted;
  }, [filteredData, tableColumns, sortKey, sortOrder]);

  const rowHeight = 44;
  const { focusedRowId, handleTableKeyDown, handleRowClick } = useListingTableFocus<Task>({
    rows: sortedData,
    getRowId: (row) => row.id,
    containerRef: tableScrollRef,
    rowHeight,
  });

  const overscan = 6;
  const totalRows = sortedData.length;
  const visibleRange = useMemo(() => {
    if (!viewportHeight || totalRows === 0) {
      return { start: 0, end: totalRows, topPad: 0, bottomPad: 0 };
    }
    const maxStart = Math.max(0, totalRows - 1);
    const rawStart = Math.min(
      maxStart,
      Math.max(0, Math.floor(scrollTop / rowHeight) - overscan)
    );
    const rawEnd = Math.min(
      totalRows,
      Math.ceil((scrollTop + viewportHeight) / rowHeight) + overscan
    );
    const start = Math.min(rawStart, totalRows);
    const end = Math.max(start, rawEnd);
    const topPad = start * rowHeight;
    const bottomPad = Math.max(0, (totalRows - end) * rowHeight);
    return { start, end, topPad, bottomPad };
  }, [overscan, rowHeight, scrollTop, totalRows, viewportHeight]);
  const visibleRows = useMemo(
    () => sortedData.slice(visibleRange.start, visibleRange.end),
    [sortedData, visibleRange]
  );

  const handleSort = (key: string, sorter?: unknown) => {
    if (typeof sorter !== 'function') {
      return;
    }
    if (sortKey !== key) {
      setSortKey(key);
      setSortOrder('ascend');
      persistSort(key, 'ascend');
      return;
    }
    if (sortOrder === 'ascend') {
      setSortOrder('descend');
      persistSort(key, 'descend');
      return;
    }
    setSortKey(null);
    setSortOrder(null);
    persistSort(null, null);
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Space size={4} wrap>
          <Button
            type={filter === 'all' ? 'primary' : 'default'}
            onClick={() => setFilter('all')}
          >
            All
          </Button>
          <Button
            type={filter === 'ongoing' ? 'primary' : 'default'}
            onClick={() => setFilter('ongoing')}
          >
            Ongoing ({statusCounts.ongoing})
          </Button>
          <Button
            type={filter === 'complete' ? 'primary' : 'default'}
            onClick={() => setFilter('complete')}
          >
            Complete ({statusCounts.complete})
          </Button>
        </Space>
        <Space>
          <Button
            icon={<ColumnHeightOutlined />}
            onClick={() => {
              setPendingVisibleKeys(visibleKeys);
              setColumnsSearch('');
              setColumnsModalOpen(true);
            }}
          >
            Columns
          </Button>
          {canDelete && (selectAll || selectedRowKeys.length > 0) && (
            <Popconfirm
              title={selectAll ? 'Delete all tasks?' : `Delete ${selectedRowKeys.length} task${selectedRowKeys.length === 1 ? '' : 's'}?`}
              okText="Delete"
              okButtonProps={{ danger: true, loading: deleting }}
              onConfirm={handleBatchDelete}
            >
              <Button danger loading={deleting}>
                Batch Delete
              </Button>
            </Popconfirm>
          )}
          {(selectAll || selectedRowKeys.length > 0) && (
            <Button
              icon={<span style={{ color: '#9254de' }}><DownloadOutlined /></span>}
              onClick={handleBatchExport}
              style={{
                borderColor: '#9254de',
                color: '#9254de',
              }}
            >
              Batch Export
            </Button>
          )}
          {canCreate && (
            <Button icon={<UploadOutlined />} onClick={handleImportNavigate}>
              Import
            </Button>
          )}
          {canCreate && (
            <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/tasks/new')}>
              New Task
            </Button>
          )}
        </Space>
      </div>
      <Modal
        title="Select Columns"
        open={columnsModalOpen}
        onCancel={() => setColumnsModalOpen(false)}
        width={820}
        footer={(
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Button
              type="link"
              onClick={() => {
                setPendingVisibleKeys(defaultVisibleKeys);
              }}
            >
              Reset to default
            </Button>
            <Space>
              <Button onClick={() => setColumnsModalOpen(false)}>Cancel</Button>
              <Button
                type="primary"
                onClick={() => {
                  handleColumnVisibilityChangeLimited(pendingVisibleKeys);
                  setColumnsModalOpen(false);
                }}
              >
                Apply
              </Button>
            </Space>
          </div>
        )}
      >
        <Input
          placeholder="Search Columns"
          prefix={<SearchOutlined />}
          value={columnsSearch}
          onChange={(event) => setColumnsSearch(event.target.value)}
          style={{ marginBottom: 16 }}
        />
        {columnGroups.map((group) => {
          const groupColumns = group.keys
            .map((key) => columnConfigByKey.get(key))
            .filter((col): col is typeof columnConfig[number] => Boolean(col))
            .filter((col) => filteredColumnConfig.some((filtered) => filtered.key === col.key));

          if (groupColumns.length === 0) {
            return null;
          }

          return (
            <div key={group.title} style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 8 }}>
                {group.title.toUpperCase()}
              </div>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                  gap: 12,
                }}
              >
                {groupColumns.map((col) => (
                  <label key={col.key} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <AntCheckbox
                      checked={pendingVisibleKeys.includes(col.key)}
                      disabled={columnSelectionLocked && !pendingVisibleKeys.includes(col.key)}
                      onChange={(event) => {
                        if (event.target.checked) {
                          setPendingVisibleKeys((prev) => [...prev, col.key]);
                        } else {
                          setPendingVisibleKeys((prev) => prev.filter((key) => key !== col.key));
                        }
                      }}
                    />
                    <span style={{ color: columnSelectionLocked && !pendingVisibleKeys.includes(col.key) ? '#9ca3af' : undefined }}>
                      {col.title}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          );
        })}
      </Modal>
      {submitNotice && (
        <div style={{ marginBottom: 16 }}>
          <Alert color={submitNotice.type} onDismiss={() => setSubmitNotice(null)}>
            {submitNotice.message}
          </Alert>
        </div>
      )}
      <div className="flowbite-listing-table-shell">
        <div style={{ paddingRight: scrollbarWidth }}>
          <FlowbiteTable className="flowbite-listing-table header-only w-full table-fixed" hoverable>
            <colgroup>
              <col style={{ width: 48 }} />
              {tableColumns.map((col, index) => (
                <col key={`head_${String(col.key)}`} style={{ width: columnWidths[index] }} />
              ))}
            </colgroup>
            <TableHead>
              <TableRow>
                <TableHeadCell className="w-12">
                  <Checkbox
                    checked={selectAll || (sortedData.length > 0 && selectedRowKeys.length === sortedData.length)}
                    onClick={(event) => event.stopPropagation()}
                    onChange={(e) => {
                      e.stopPropagation();
                      if (e.target.checked) {
                        setSelectAll(true);
                        setExcludedRowKeys([]);
                        setSelectedRowKeys([]);
                      } else {
                        setSelectAll(false);
                        setExcludedRowKeys([]);
                        setSelectedRowKeys([]);
                      }
                    }}
                  />
                </TableHeadCell>
                {tableColumns.map((col, index) => {
                  const key = String(col.key);
                  const isSortable = typeof col.sorter === 'function';
                  const isSorted = sortKey === key;
                  const sortIndicator =
                    isSorted && sortOrder
                      ? sortOrder === 'ascend'
                        ? '▲'
                        : '▼'
                      : '';
                  const isFirstDataColumn = index === 0;
                  const maxWidth =
                    availableWidth != null
                      ? Math.max(minColumnWidth, availableWidth - minColumnWidth * (columnWidths.length - 1))
                      : undefined;

                  return (
                    <FlowbiteResizableHeader
                      key={key}
                      width={columnWidths[index]}
                      maxWidth={maxWidth}
                      onResize={handleResize(index)}
                      onClick={() => handleSort(key, col.sorter)}
                      className={isSortable ? 'cursor-pointer select-none' : undefined}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div>{col.title}</div>
                          {isFirstDataColumn && (
                            <div className="mt-1 text-xs text-gray-400">{totalLabel}</div>
                          )}
                        </div>
                        {isSortable && (
                          <span className="text-xs text-gray-400">{sortIndicator}</span>
                        )}
                      </div>
                    </FlowbiteResizableHeader>
                  );
                })}
              </TableRow>
              <TableRow>
                <TableHeadCell className="w-12" />
                {tableColumns.map((col, index) => {
                  const key = String(col.key);
                  return (
                    <TableHeadCell key={`${key}-search`} className="py-2">
                      {searchRowCells?.[index] ?? null}
                    </TableHeadCell>
                  );
                })}
              </TableRow>
            </TableHead>
          </FlowbiteTable>
        </div>
        <div
          className="flowbite-listing-table-scroll"
          ref={tableScrollRef}
          tabIndex={0}
          onKeyDown={handleTableKeyDown}
          style={
            viewportHeight
              ? {
                  height: viewportHeight,
                  overflowY: 'auto',
                  overflowX: 'hidden',
                }
              : { overflowY: 'auto', overflowX: 'hidden' }
          }
        >
          <FlowbiteTable className="flowbite-listing-table w-full table-fixed" hoverable>
            <colgroup>
              <col style={{ width: 48 }} />
              {tableColumns.map((col, index) => (
                <col key={`body_${String(col.key)}`} style={{ width: columnWidths[index] }} />
              ))}
            </colgroup>
            <TableBody className="divide-y">
              {loading && (
                <TableRow>
                  <TableCell colSpan={tableColumns.length + 1} className="py-8 text-center text-sm text-gray-500">
                    Loading...
                  </TableCell>
                </TableRow>
              )}
              {!loading && sortedData.length === 0 && (
                <TableRow>
                  <TableCell colSpan={tableColumns.length + 1} className="py-8 text-center text-sm text-gray-500">
                    No tasks
                  </TableCell>
                </TableRow>
              )}
              {!loading && sortedData.length > 0 && (
                <>
                  {visibleRange.topPad > 0 && (
                    <TableRow>
                      <TableCell
                        colSpan={tableColumns.length + 1}
                        style={{ height: visibleRange.topPad, padding: 0 }}
                      />
                    </TableRow>
                  )}
                  {visibleRows.map((record: Task, index: number) => {
                    const isSelected = selectedRowKeys.includes(record.id);
                    const isFocused = focusedRowId === record.id;
                    return (
                      <TableRow
                        key={record.id}
                        data-row-id={record.id}
                        className={`hover:bg-gray-50 ${isFocused ? 'bg-blue-50' : ''}`}
                        onDoubleClick={canView ? () => navigate(`/tasks/${record.id}/edit`) : undefined}
                        onClick={() => handleRowClick(record.id)}
                      >
                        <TableCell className="w-12">
                          <Checkbox
                            checked={selectAll ? !excludedRowKeys.includes(record.id) : isSelected}
                            onClick={(event) => event.stopPropagation()}
                            onChange={() => {
                              if (selectAll) {
                                setExcludedRowKeys((prev) =>
                                  prev.includes(record.id)
                                    ? prev.filter((key) => key !== record.id)
                                    : [...prev, record.id]
                                );
                                return;
                              }
                              setSelectedRowKeys((prev) =>
                                prev.includes(record.id)
                                  ? prev.filter((key) => key !== record.id)
                                  : [...prev, record.id]
                              );
                            }}
                          />
                        </TableCell>
                        {tableColumns.map((col, index) => {
                          const value = getCellValue(record, col);
                          const content =
                            typeof col.render === 'function'
                              ? col.render(value, record, index)
                              : value ?? '-';
                          return (
                            <TableCell key={`${record.id}_${String(col.key)}`} style={{ width: columnWidths[index] }}>
                              {content}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    );
                  })}
                  {visibleRange.bottomPad > 0 && (
                    <TableRow>
                      <TableCell
                        colSpan={tableColumns.length + 1}
                        style={{ height: visibleRange.bottomPad, padding: 0 }}
                      />
                    </TableRow>
                  )}
                </>
              )}
            </TableBody>
          </FlowbiteTable>
        </div>
      </div>
    </div>
  );
};
